import { useGetAllProducts, useGetAllCategories } from "@/api/queries";
import { ProductCard } from "@/components/atoms";
import { Button } from "@/components/ui";
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { getProductAvailability } from "@/lib/utils";

export const MoreProducts = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState<string>("all");

  // Use the API with category filtering
  const filters = useMemo(() => {
    return activeCategory === "all" ? undefined : { categoryId: activeCategory };
  }, [activeCategory]);

  const { data, isFetching } = useGetAllProducts(filters);
  const { data: categoriesData, isLoading: categoriesLoading } = useGetAllCategories();

  // Get products from the first page of API data
  const apiProducts = data?.pages?.[0]?.data?.data?.products || [];

  // Limit to 12 products for display
  const displayedProducts = apiProducts.slice(0, 12);

  // Get available categories from API
  const availableCategories = categoriesData?.data || [];

  // Handle category selection
  const handleCategoryChange = (categoryId: string) => {
    setActiveCategory(categoryId);
  };

  const handleViewAll = () => {
    navigate("/shop");
  };

  return (
    <section className='py-16 bg-gray-50'>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-10'>
          <h2 className='text-3xl font-bold text-gray-800 mb-2'>More Products From Us</h2>
          <p className='text-gray-600 max-w-2xl mx-auto'>
            Discover our wide range of premium products designed for your lifestyle needs.
          </p>
        </div>

        {/* Category Tabs with All button */}
        <div className='flex flex-wrap justify-center mb-8 gap-2'>
          {/* All Categories Button */}
          <button
            onClick={() => handleCategoryChange("all")}
            className={`px-4 py-2 rounded-full transition-colors ${
              activeCategory === "all"
                ? "bg-primary text-white"
                : "bg-white text-gray-600 hover:bg-gray-100"
            }`}
          >
            All
          </button>

          {/* Category Specific Buttons */}
          {!categoriesLoading &&
            availableCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryChange(category.id)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  activeCategory === category.id
                    ? "bg-primary text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100"
                }`}
              >
                {category.name}
                {category.count && (
                  <span className='ml-1 text-xs opacity-75'>({category.count})</span>
                )}
              </button>
            ))}

          {/* Loading categories indicator */}
          {categoriesLoading && (
            <div className='flex items-center space-x-2 px-4 py-2'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span className='text-sm text-gray-500'>Loading categories...</span>
            </div>
          )}
        </div>

        {/* Loading State */}
        {isFetching && (
          <div className='text-center py-8'>
            <div className='flex justify-center items-center space-x-2'>
              <Loader2 className='h-6 w-6 animate-spin' />
              <p className='text-gray-600'>Loading products...</p>
            </div>
          </div>
        )}

        {/* Products Grid */}
        {!isFetching && displayedProducts.length > 0 && (
          <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6'>
            {displayedProducts?.map((product) => {
              const { hasStock, isUnlimited } = getProductAvailability(product);
              return (
                <ProductCard
                  key={product.id}
                  id={product.id}
                  name={product.name}
                  image={product.images?.[0]?.url}
                  price={product.finalPrice}
                  oldPrice={product.discountPercentage > 0 ? product.basePrice : undefined}
                  hasStock={hasStock}
                  isUnlimited={isUnlimited}
                  discount={product.discountPercentage}
                  sku={product.sku}
                />
              );
            })}
          </div>
        )}

        {/* No Products Message */}
        {!isFetching && displayedProducts.length === 0 && (
          <div className='text-center py-8'>
            <div className='text-6xl mb-4'>📦</div>
            <p className='text-gray-600 text-lg'>No products found in this category.</p>
            <p className='text-gray-500 text-sm mt-2'>
              Try selecting a different category or check back later.
            </p>
          </div>
        )}

        {/* View All Button */}
        <div className='text-center mt-10'>
          <Button onClick={handleViewAll} size='lg' className='px-8'>
            View All Products
          </Button>
        </div>
      </div>
    </section>
  );
};
